import { useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { PK_BY_COORDINATES } from '../gql/queries/geoPositions/geoPositionRoads';
import { pkFromMeshEntries } from '../utils/pkFromMesh';
import { getCachedRoadMeshEntries, hydrateRoadMeshes } from '../offline/roadMeshStore';
import { getSeedKey } from '../gql/cacheSeed';
import { withTimeout, NetworkTimeoutError } from '../utils/withTimeout';
import { isEffectivelyOnline } from '../offline/connectivity';

// ═══════════════════════════════════════════════════════════════════
// useGpsToPK — V4 D13 (2026-04-22)
// ─────────────────────────────────────────────────────────────────
// Wrapper del query GraphQL `pkByCoordinates` (backend
// `geoPositionRoad.service.ts#findPkByCoordinates`). Convierte coordenadas GPS
// a PK + metros, y desde multi-carretera (2026-08-15) dice además en QUÉ
// carretera del recorrido cayó el punto.
//
// Uso: en los modales del timer para prefill de PK inicial/final.
//   const resolvePk = useGpsToPK();
//   const pk = await resolvePk({ lat, lng, roadIds, preferredRoadId });
//   if (pk) { setPkKm(pk.pkKm); setRoadId(pk.roadId); }
//   // null = el punto GPS está fuera del trazado (>300m del vértice más cercano).
//
// Por qué N carreteras: un PK no identifica un sitio. La C-25 y su ramal C-25z
// comparten kilometraje sobre trazados que se separan hasta 2,76 km, así que el
// PK 235+000 existe en las dos y son puntos a 874 m. Pasada la bifurcación la
// traza más cercana resuelve la ambigüedad sola; en los 400 m que comparten
// manda `preferredRoadId` (la carretera del tramo en curso).
//
// Cache local con throttle para evitar round-trips innecesarios durante un
// tramo activo donde el GPS reporta coordenadas muy similares cada pocos segundos.
//
// 🔴 LA MALLA PRIMERO, LA RED DESPUÉS (2026-09-08).
//
// La malla se descarga al arrancar la app y vive en IndexedDB
// (`roadMeshStore` -> `pkFromMeshEntries`, <=300 m): es local, instantánea y
// da la misma respuesta que el backend. Se mira ANTES de preguntar a la red.
//
// Antes era al revés —la malla solo entraba sin red (`navigator.onLine ===
// false`) o si la query fallaba— y en carretera eso se traducía en un campo
// vacío durante segundos: con una raya de cobertura la tablet sigue «online»,
// la petición no tenía tope y se quedaba colgada hasta que el sistema se
// rendía (medido el 2026-09-08 con el backend en el portátil y Atlas por el
// hotspot: consultas de 64 ms a 4,4 s, seis «Server selection timeout» y seis
// `ECONNRESET` en una mañana; el driver de Mongo espera 30 s). Solo ENTONCES
// se consultaba la malla que la tablet llevaba descargada desde el arranque.
//
// La red queda para cuando no hay malla o el punto cae fuera de ella, y con un
// tope: mejor un campo vacío a los 3 s que a los 30.
//
// 🔴 La malla ya NO se precachea por pantalla: se descarga al arrancar la app
// y vive en IndexedDB. Antes era un `Map` en memoria que moria al recargar,
// solo guardaba la calzada del tramo activo y solo se llenaba desde la pantalla
// de detalle del recorrido — asi que sin cobertura no salia el PK, que es
// justo cuando hace falta.
// ═══════════════════════════════════════════════════════════════════

export type GpsToPkInput = {
  lat: number;
  lng: number;
  /**
   * Carreteras del recorrido (multi-carretera 2026-08-15). Se busca en TODAS y
   * el resultado dice en cuál cayó — pasada una bifurcación las trazas se
   * separan cientos de metros, así que el operario no tiene que elegir nada.
   */
  roadIds: string[];
  /**
   * Con qué carretera desempatar en el tramo que dos COMPARTEN, donde los
   * candidatos quedan a metros: la del tramo en curso. Sin esto el tramo podría
   * saltar de carretera por el temblor del GPS.
   */
  preferredRoadId?: string | null;
  /**
   * Vías que NO son del recorrido donde buscar si ninguna de `roadIds` explica
   * el punto (2026-09-23): la patrulla que se va a apoyar al otro tramo. Las da
   * quien llama —`useSupportRoadCandidates`: las de la concesión del recorrido,
   * que son las únicas que el servidor acepta añadir—.
   *
   * Es un superconjunto estricto de la búsqueda de siempre: las vías del
   * recorrido se miran primero y ganan aunque otra quede más cerca, así que
   * sobre ellas la respuesta es idéntica a la de antes. Solo donde antes había
   * `null` puede aparecer una vía nueva, marcada con `outsideRoute`.
   *
   * Solo contra la malla local, que `ensureRoadMesh` baja entera al arrancar:
   * es la que funciona donde se necesita, sin cobertura. Y NO viaja a la red —
   * el esquema de `pkByCoordinates` no la conoce—.
   */
  otherRoadIds?: string[];
};

export type GpsToPkResult = {
  pkKm: number;
  pkMeter: number;
  distance: number;
  /** En qué carretera cayó el punto. */
  roadId: string;
  /** La vía NO es del recorrido (solo puede pasar con `otherRoadIds`). */
  outsideRoute?: boolean;
};

const CACHE_TTL_MS = 5_000; // 5s — 1 llamada por ubicación/road en ese intervalo.
const COORDINATE_PRECISION = 5; // ~1m de precisión (lat/lng redondeo a 5 decimales).
/**
 * Tope de la consulta de red. Solo se llega aquí sin malla o con el punto fuera
 * de ella; pasado esto el campo se queda vacío y el operario lo teclea, que es
 * mejor que esperar los 30 s del driver de Mongo con la tablet en la mano.
 */
const NETWORK_TIMEOUT_MS = 3_000;

type CacheEntry = {
  value: GpsToPkResult | null;
  timestamp: number;
};

function cacheKey(input: GpsToPkInput): string {
  const lat = input.lat.toFixed(COORDINATE_PRECISION);
  const lng = input.lng.toFixed(COORDINATE_PRECISION);
  // La preferencia entra en la clave: en el tramo compartido cambia la
  // respuesta, así que dos tramos distintos no pueden compartir entrada.
  const otherKey = input.otherRoadIds?.length
    ? `+${[...input.otherRoadIds].sort().join('|')}`
    : '';
  return `${[...input.roadIds].sort().join('|')}${otherKey}@${input.preferredRoadId ?? ''}:${lat},${lng}`;
}

/**
 * El punto sobre alguna de las vías candidatas que NO son del recorrido. Sin
 * preferencia: fuera del recorrido no hay «vía del tramo» con la que desempatar.
 */
function resolveOnOtherRoads(input: GpsToPkInput): GpsToPkResult | null {
  const ownRoadIds = new Set(input.roadIds);
  const candidateIds = (input.otherRoadIds ?? []).filter((id) => !ownRoadIds.has(id));
  if (candidateIds.length === 0) return null;
  const otherKey = getCachedRoadMeshEntries(candidateIds);
  if (otherKey.length === 0) return null;
  const hit = pkFromMeshEntries(input.lat, input.lng, otherKey, null);
  return hit ? { ...hit, outsideRoute: true } : null;
}

export function useGpsToPK(): (
  input: GpsToPkInput,
) => Promise<GpsToPkResult | null> {
  const client = useApolloClient();
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());

  return useCallback(
    async (input: GpsToPkInput): Promise<GpsToPkResult | null> => {
      const key = cacheKey(input);
      const cached = cacheRef.current.get(key);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.value;
      }

      // ── 1 · La malla, PRIMERO ─────────────────────────────────────────
      //
      // Hidratar por si la app arrancó SIN cobertura y el índice en memoria
      // sigue vacío (al reabrir la app la copia está en disco, la memoria no).
      // Es idempotente: ya hidratada, no relee nada.
      const ownerKey = getSeedKey();
      if (ownerKey) await hydrateRoadMeshes(ownerKey);

      // Por CALZADA, no una polilínea por carretera: las dos son paralelas
      // y concatenarlas daría proyecciones sin sentido. Es lo que permite
      // que el cambio de sentido deje de quedarse sin malla.
      const routeEntries = getCachedRoadMeshEntries(input.roadIds);
      const local = pkFromMeshEntries(
        input.lat,
        input.lng,
        routeEntries,
        input.preferredRoadId
      );
      if (local) {
        cacheRef.current.set(key, { value: local, timestamp: Date.now() });
        return local;
      }
      // Un null de la malla NO se cachea: solo dice «no hay malla para esto o
      // el punto cae fuera de la ventana de 300 m», y la red aún puede saber.

      // ── 1b · Las demás vías, si se pidió ────────────────────────────────
      //
      // Con la malla del recorrido PRESENTE, su `null` ya es la respuesta: la
      // red sabe lo mismo que la malla (`pkByCoordinates` proyecta igual), así
      // que se mira fuera antes de gastar el tope de 3 s. Sin esa malla la
      // red va primero —podría situar el punto en el recorrido— y las demás
      // vías quedan de último recurso, abajo.
      const otherRoads = () => resolveOnOtherRoads(input);
      if (routeEntries.length > 0) {
        const supportHit = otherRoads();
        if (supportHit) {
          cacheRef.current.set(key, { value: supportHit, timestamp: Date.now() });
          return supportHit;
        }
      }

      // ── 2 · Sin malla, o el punto fuera de ella: la red, con tope ─────
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return routeEntries.length > 0 ? null : otherRoads();
      }

      try {
        const { data } = await withTimeout(
          client.query<{ pkByCoordinates: GpsToPkResult | null }>({
            query: PK_BY_COORDINATES,
            // Solo lo que el esquema del servidor conoce: con una opción de más
            // GraphQL rechazaría la consulta entera.
            variables: {
              input: {
                lat: input.lat,
                lng: input.lng,
                roadIds: input.roadIds,
                preferredRoadId: input.preferredRoadId,
              },
            },
            fetchPolicy: 'network-only',
          }),
          NETWORK_TIMEOUT_MS,
        );
        // Aquí sí se cachea el null: es una respuesta real del backend,
        // «fuera de trazado».
        const value =
          data?.pkByCoordinates ??
          (routeEntries.length > 0 ? null : otherRoads());
        cacheRef.current.set(key, { value, timestamp: Date.now() });
        return value;
      } catch (err) {
        // El tope agotado es lo esperado con poca cobertura: no es un
        // incidente y llenaría Sentry de ruido en cada bache.
        // Y con el modo «sin servidor» tampoco: el fallo es lo esperado, y
        // ahora llega al momento en vez de agotar el tope (spec 2026-10-09 § 9).
        if (!(err instanceof NetworkTimeoutError) && isEffectivelyOnline()) {
          Sentry.captureException(err, {
            tags: { feature: 'surveillance-route', hook: 'useGpsToPK' },
            extra: { input },
          });
        }
        // Sin respuesta de la red: si no había malla del recorrido que
        // consultar, las demás vías son lo único que queda.
        return routeEntries.length > 0 ? null : otherRoads();
      }
    },
    [client],
  );
}

export default useGpsToPK;
